import { useCallback, useEffect, useState } from 'react';

import { api, downloadFile, mutate, uploadFile } from '../shared/api';
import { formatDate, formatDateTime, formatMoney, localIsoDate } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';
import { arrow, canConfirm, confirmBody, consequence, lastRecalc, MIN_REASON_LENGTH, originalFor, recalcButtonState, type RecalcInfo, type RecalcPreview } from './inventoryDailyRecalc';

type Locale = 'hr' | 'en';

type Warehouse = { code: string; name: string; inventory_lock?: boolean };
type Cause = { code: string; label_hr: string; label_en: string };
type InventoryType = 'operativna' | 'godisnja' | 'izvanredna';
type SessionStatus = 'draft' | 'recount' | 'submitted' | 'reviewed' | 'approved' | 'posted';
type Resolution = 'otpis' | 'teret_odgovorne_osobe' | 'teret_drustva';

/**
 * Stavka kakvu vrati /sessions/{id}. Kod slijepog brojanja (draft i recount) backend
 * izostavlja sve što bi odalo knjigovodstveno stanje, pa su ta polja opcionalna.
 */
type SessionItem = {
    id: number;
    item_code: string;
    item_name: string | null;
    counted_qty: number;
    cause_code: string | null;
    cause_note: string | null;
    blind?: boolean;
    recount_qty?: number | null;
    recount_required?: boolean;
    expected_qty?: number;
    expected_source?: 'manual' | 'snapshot' | 'snapshot_frozen';
    expected_override_reason?: string | null;
    first_counted_qty?: number | null;
    drift_qty?: number;
    drift_note?: string | null;
    diff_qty?: number;
    unit_price_eur?: number | null;
    diff_value_eur?: number;
    responsible_user_id?: number | null;
    resolution?: Resolution | null;
};

type SessionRow = {
    id: number;
    public_id: string;
    warehouse_code: string;
    warehouse_name: string | null;
    inventory_type: InventoryType;
    inventory_date: string;
    status: SessionStatus;
    committee_note: string | null;
    created_by: number;
    submitted_at: string | null;
    reviewed_at: string | null;
    review_note: string | null;
    approved_at: string | null;
    posted_at: string | null;
    gk_reference: string | null;
    total_diff_value_eur: number | null;
    zapisnik_pdf_path: string | null;
    blind?: boolean;
    warehouse_locked?: boolean;
    recalc?: RecalcInfo | null;
    items?: SessionItem[];
};

/** Redak rekonsilijacije iz /submit-preview — ovo je prvi put da brojač vidi stanje. */
type ReconRow = {
    item_id: number | null;
    item_code: string;
    item_name: string | null;
    entered_expected_qty: number | null;
    frozen_expected_qty: number | null;
    current_expected_qty: number | null;
    expected_qty: number;
    first_counted_qty: number | null;
    recount_qty: number | null;
    counted_qty: number | null;
    diff_qty: number | null;
    unit_price_eur: number;
    diff_value_eur: number | null;
    drift_qty: number;
    drift_note: string | null;
    cause_code: string | null;
    cause_note: string | null;
    status: 'ok' | 'diff' | 'recount' | 'missing' | 'unknown';
    needs_note: boolean;
    needs_cause: boolean;
};

type ReconTotals = {
    frozen_batch_id: number | null;
    current_batch_id: number | null;
    items_total: number;
    items_with_diff: number;
    items_drifted: number;
    items_missing: number;
    items_unknown: number;
    items_recount: number;
    total_diff_value_eur: number;
    surplus_value_eur: number;
    shortage_value_eur: number;
    thresholds: { qty_abs: number; qty_pct: number; value_eur: number };
};

type Blocking = {
    missing_cause?: { item_id: number; item_code: string }[];
    missing_note?: { item_id: number; item_code: string; razlog: string }[];
    submit_note?: string;
};

type Preview = {
    session_id: number;
    status: SessionStatus;
    rows: ReconRow[];
    totals: ReconTotals;
    blocking: Blocking;
    min_note_length: number;
};

const BTN: React.CSSProperties = { padding: '6px 14px', background: '#3a5a3a', color: '#f0fbf0', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const INP: React.CSSProperties = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 6, fontSize: '0.85rem' };
const card: React.CSSProperties = { background: '#fff', border: '1px solid #cfe0cf', borderRadius: 10, padding: 16, marginBottom: 16 };
const TH: React.CSSProperties = { background: '#e7f0e7', color: '#2f4d2f' };

const fmtQty = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('hr-HR', { minimumFractionDigits: 0, maximumFractionDigits: 3 }));

function STATUS_LABEL(s: SessionStatus, locale: Locale): string {
    const m: Record<SessionStatus, [string, string]> = {
        draft: ['U izradi', 'Draft'],
        recount: ['Ponovno brojanje', 'Recount'],
        submitted: ['Predano na pregled', 'Submitted'],
        reviewed: ['Pregledano', 'Reviewed'],
        approved: ['Odobreno', 'Approved'],
        posted: ['Knjiženo', 'Posted'],
    };
    return locale === 'en' ? m[s][1] : m[s][0];
}
const STATUS_COLOR: Record<SessionStatus, string> = { draft: '#888', recount: '#b1602f', submitted: '#b98b1f', reviewed: '#2f6e85', approved: '#1f7a36', posted: '#2f4d2f' };

/** Klasifikacija retka rekonsilijacije - boja i kratka oznaka u pregledu prije predaje. */
function RECON_STATUS(s: ReconRow['status'], locale: Locale): { label: string; color: string } {
    const m: Record<ReconRow['status'], [string, string, string]> = {
        ok: ['Slaže se', 'Matches', '#1f7a36'],
        diff: ['Razlika', 'Difference', '#b98b1f'],
        recount: ['Ponovno brojanje', 'Recount', '#b1602f'],
        missing: ['Nije popisano', 'Not counted', '#8a3a3a'],
        unknown: ['Nema u stanju', 'Not in stock', '#8a3a3a'],
    };
    return { label: locale === 'en' ? m[s][1] : m[s][0], color: m[s][2] };
}

function TYPE_LABEL(v: InventoryType, locale: Locale): string {
    const m: Record<InventoryType, [string, string]> = {
        operativna: ['Operativna', 'Operational'],
        godisnja: ['Godišnja', 'Annual'],
        izvanredna: ['Izvanredna', 'Extraordinary'],
    };
    return locale === 'en' ? m[v][1] : m[v][0];
}

function RESOLUTION_LABEL(v: Resolution | null, locale: Locale): string {
    if (!v) return '—';
    const m: Record<Resolution, [string, string]> = {
        otpis: ['Otpis', 'Write-off'],
        teret_odgovorne_osobe: ['Teret odgovorne osobe', 'Charged to responsible person'],
        teret_drustva: ['Teret društva', 'Charged to company'],
    };
    return locale === 'en' ? m[v][1] : m[v][0];
}

// Slijepo brojanje: knjigovodstveno stanje se u unos više ne upisuje niti prikazuje.
const emptyItemForm = { item_code: '', item_name: '', counted_qty: '', unit_price_eur: '', cause_code: '', cause_note: '' };

export function InventoryDailyModule({ token, locale = 'hr', onBack }: { token: string; locale?: Locale; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [causes, setCauses] = useState<Cause[]>([]);
    const [view, setView] = useState<'list' | 'detail'>('list');
    const [error, setError] = useState<string | null>(null);

    // list
    const [sessions, setSessions] = useState<SessionRow[]>([]);
    const [loadingList, setLoadingList] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [filterWarehouse, setFilterWarehouse] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [showNewForm, setShowNewForm] = useState(false);
    const [newForm, setNewForm] = useState({ warehouse_code: '', inventory_type: 'operativna' as InventoryType, inventory_date: localIsoDate(), committee_note: '', lock_warehouse: false });
    const [creating, setCreating] = useState(false);

    // detail
    const [detail, setDetail] = useState<SessionRow | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [busy, setBusy] = useState(false);

    // item form
    const [itemForm, setItemForm] = useState(emptyItemForm);
    const [savingItem, setSavingItem] = useState(false);

    // cause admin add
    const [showCauseForm, setShowCauseForm] = useState(false);
    const [causeForm, setCauseForm] = useState({ code: '', label_hr: '', label_en: '' });
    const [savingCause, setSavingCause] = useState(false);

    // review
    const [reviewNote, setReviewNote] = useState('');

    // approve
    const [resolutions, setResolutions] = useState<Record<number, Resolution | ''>>({});

    // post
    const [gkReference, setGkReference] = useState('');

    // zapisnik
    const [zapisnikFile, setZapisnikFile] = useState<File | null>(null);
    const [uploadingZapisnik, setUploadingZapisnik] = useState(false);

    // pregled prije predaje (rekonsilijacija) — ovdje se stanje prvi put otkriva
    const [preview, setPreview] = useState<Preview | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [submitNote, setSubmitNote] = useState('');
    const [driftNotes, setDriftNotes] = useState<Record<number, string>>({});

    // ponovno brojanje
    const [recountQty, setRecountQty] = useState<Record<number, string>>({});

    // UID746: rekalkulacija na noviji Faros batch
    const [recalcPreview, setRecalcPreview] = useState<RecalcPreview | null>(null);
    const [recalcReason, setRecalcReason] = useState('');
    const [loadingRecalc, setLoadingRecalc] = useState(false);

    const reloadCauses = useCallback(async () => {
        const c = await api<{ data: Cause[] }>('/inventory-daily/cause-lookup', token);
        setCauses(c.data);
    }, [token]);

    /** Popis skladišta nosi i oznaku statusa INVENTURA, pa se osvježava nakon zaključavanja. */
    const reloadWarehouses = useCallback(async () => {
        const w = await api<{ data: Warehouse[]; locked_codes: string[] }>('/inventory-daily/warehouses', token);
        setWarehouses(w.data);
    }, [token]);

    useEffect(() => {
        void (async () => {
            try {
                await reloadWarehouses();
                await reloadCauses();
            } catch (e: any) {
                setError(String(e?.message ?? e));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const reloadList = useCallback(async () => {
        setLoadingList(true);
        try {
            const qs = new URLSearchParams();
            if (filterStatus) qs.set('status', filterStatus);
            if (filterWarehouse) qs.set('warehouse_code', filterWarehouse);
            if (filterDateFrom) qs.set('date_from', filterDateFrom);
            if (filterDateTo) qs.set('date_to', filterDateTo);
            const suffix = qs.toString();
            const d = await api<{ data: SessionRow[] }>(`/inventory-daily/sessions${suffix ? `?${suffix}` : ''}`, token);
            setSessions(d.data);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setLoadingList(false);
        }
    }, [token, filterStatus, filterWarehouse, filterDateFrom, filterDateTo]);

    useEffect(() => {
        if (view === 'list') void reloadList();
    }, [view, reloadList]);

    const loadDetail = useCallback(async (id: number) => {
        setLoadingDetail(true);
        try {
            const d = await api<SessionRow>(`/inventory-daily/sessions/${id}`, token);
            setDetail(d);
            const res: Record<number, Resolution | ''> = {};
            (d.items ?? []).forEach((i) => { res[i.id] = (i.resolution as Resolution) ?? ''; });
            setResolutions(res);
            setGkReference(d.gk_reference ?? '');
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setLoadingDetail(false);
        }
    }, [token]);

    const openDetail = (id: number) => {
        setError(null);
        setItemForm(emptyItemForm);
        setPreview(null);
        setSubmitNote('');
        setDriftNotes({});
        setRecountQty({});
        setRecalcPreview(null);
        setRecalcReason('');
        setView('detail');
        void loadDetail(id);
    };

    const backToList = () => {
        setView('list');
        setDetail(null);
        setError(null);
        void reloadList();
    };

    const createSession = async () => {
        if (!newForm.warehouse_code || !newForm.inventory_date) {
            setError(t('Odaberi skladište i datum.', 'Pick a warehouse and date.'));
            return;
        }
        setCreating(true);
        setError(null);
        try {
            const r = await mutate<{ id: number }>('/inventory-daily/sessions', token, 'POST', {
                warehouse_code: newForm.warehouse_code,
                inventory_type: newForm.inventory_type,
                inventory_date: newForm.inventory_date,
                committee_note: newForm.committee_note || null,
                lock_warehouse: newForm.lock_warehouse,
            });
            setShowNewForm(false);
            setNewForm({ warehouse_code: '', inventory_type: 'operativna', inventory_date: localIsoDate(), committee_note: '', lock_warehouse: false });
            await reloadWarehouses();
            openDetail(r.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setCreating(false);
        }
    };

    const saveItem = async () => {
        if (!detail) return;
        if (!itemForm.item_code || itemForm.counted_qty === '') {
            setError(t('Šifra artikla i izbrojana količina su obavezni.', 'Item code and counted qty are required.'));
            return;
        }
        setSavingItem(true);
        setError(null);
        try {
            // Knjig. stanje se namjerno NE šalje: referentna vrijednost je zamrznuti
            // snapshot, a usporedba se radi tek kod predaje.
            const payload: Record<string, unknown> = {
                item_code: itemForm.item_code,
                item_name: itemForm.item_name || null,
                counted_qty: Number(itemForm.counted_qty),
                unit_price_eur: itemForm.unit_price_eur === '' ? null : Number(itemForm.unit_price_eur),
                cause_code: itemForm.cause_code || null,
                cause_note: itemForm.cause_note || null,
            };
            await mutate(`/inventory-daily/sessions/${detail.id}/items`, token, 'POST', payload);
            setItemForm(emptyItemForm);
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setSavingItem(false);
        }
    };

    const editItem = (i: SessionItem) => {
        setItemForm({
            item_code: i.item_code,
            item_name: i.item_name ?? '',
            counted_qty: String(i.counted_qty),
            unit_price_eur: i.unit_price_eur != null ? String(i.unit_price_eur) : '',
            cause_code: i.cause_code ?? '',
            cause_note: i.cause_note ?? '',
        });
    };

    const deleteItem = async (itemId: number) => {
        if (!detail) return;
        if (!confirm(t('Obrisati stavku?', 'Delete item?'))) return;
        setError(null);
        try {
            await mutate(`/inventory-daily/sessions/${detail.id}/items/${itemId}`, token, 'DELETE');
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        }
    };

    /**
     * Otvara pregled prije predaje. Ovo je trenutak u kojem se brojaču prvi put pokaže
     * knjigovodstveno stanje: do tada je brojao slijepo. Ništa se ne mijenja u bazi.
     */
    const openPreview = async () => {
        if (!detail) return;
        setLoadingPreview(true);
        setError(null);
        try {
            const p = await api<Preview>(`/inventory-daily/sessions/${detail.id}/submit-preview`, token);
            setPreview(p);
            const notes: Record<number, string> = {};
            p.rows.forEach((r) => {
                if (r.item_id !== null && r.drift_note) notes[r.item_id] = r.drift_note;
            });
            setDriftNotes((prev) => ({ ...notes, ...prev }));
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setLoadingPreview(false);
        }
    };

    /**
     * Predaja. Šalje obrazloženje predaje i napomene po stavci. Ako backend vrati 422,
     * pregled se ponovno dohvaća da se vidi točno što nedostaje.
     */
    const doSubmit = async () => {
        if (!detail) return;
        setBusy(true);
        setError(null);
        try {
            const notes = Object.entries(driftNotes)
                .filter(([, v]) => v.trim() !== '')
                .map(([k, v]) => ({ item_id: Number(k), note: v.trim() }));
            const r = await mutate<{ status: SessionStatus; message: string }>(
                `/inventory-daily/sessions/${detail.id}/submit`, token, 'POST',
                { submit_note: submitNote || null, drift_notes: notes },
            );
            setPreview(null);
            setSubmitNote('');
            await loadDetail(detail.id);
            if (r.status === 'recount') {
                setError(r.message);
            }
        } catch (e: any) {
            setError(String(e?.message ?? e));
            await openPreview();
        } finally {
            setBusy(false);
        }
    };

    /** Ponovno brojanje stavke iznad tolerancije — smije ga upisati samo druga osoba. */
    const doRecount = async (itemId: number) => {
        if (!detail) return;
        const qty = recountQty[itemId];
        if (qty === undefined || qty === '') {
            setError(t('Upiši ponovno prebrojanu količinu.', 'Enter the recounted quantity.'));
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await mutate(`/inventory-daily/sessions/${detail.id}/items/${itemId}/recount`, token, 'POST', { recount_qty: Number(qty) });
            setRecountQty((p) => { const n = { ...p }; delete n[itemId]; return n; });
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    /**
     * UID746: sažetak rekalkulacije (staro -> novo) prije potvrde. Ništa se ne mijenja
     * dok korisnik ne upiše razlog i potvrdi.
     */
    const openRecalc = async () => {
        if (!detail) return;
        setLoadingRecalc(true);
        setError(null);
        try {
            const p = await api<RecalcPreview>(`/inventory-daily/sessions/${detail.id}/recalculate-preview`, token);
            setRecalcPreview(p);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setLoadingRecalc(false);
        }
    };

    const doRecalc = async () => {
        if (!detail || !recalcPreview || !canConfirm(recalcPreview, recalcReason, busy)) return;
        setBusy(true);
        setError(null);
        try {
            await mutate(`/inventory-daily/sessions/${detail.id}/recalculate`, token, 'POST', confirmBody(recalcPreview, recalcReason));
            setRecalcPreview(null);
            setRecalcReason('');
            setPreview(null);
            await loadDetail(detail.id);
        } catch (e: any) {
            // 409 = u međuvremenu stigao noviji batch ili je netko drugi već rekalkulirao.
            setError(String(e?.message ?? e));
            await openRecalc();
        } finally {
            setBusy(false);
        }
    };

    /** Preuzimanje automatski generiranog DOCX zapisnika (nastaje kod predaje). */
    const getZapisnikDocx = async () => {
        if (!detail) return;
        setError(null);
        try {
            await downloadFile(
                `/inventory-daily/sessions/${detail.id}/zapisnik`,
                token,
                `Zapisnik-popis-${detail.warehouse_code}-${detail.inventory_date}.docx`,
            );
        } catch (e: any) {
            setError(String(e?.message ?? e));
        }
    };

    /** Ručno postavljanje/skidanje statusa INVENTURA na skladištu (administrator+). */
    const toggleWarehouseLock = async (code: string, on: boolean) => {
        setError(null);
        try {
            if (on) {
                await mutate(`/inventory-daily/warehouses/${encodeURIComponent(code)}/lock`, token, 'POST', { note: null });
            } else {
                await mutate(`/inventory-daily/warehouses/${encodeURIComponent(code)}/lock`, token, 'DELETE');
            }
            await reloadWarehouses();
            if (detail) await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        }
    };

    const doReview = async (action: 'accept' | 'reject') => {
        if (!detail) return;
        setBusy(true);
        setError(null);
        try {
            await mutate(`/inventory-daily/sessions/${detail.id}/review`, token, 'POST', { action, note: reviewNote || null });
            setReviewNote('');
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    const doApprove = async () => {
        if (!detail) return;
        const itemsWithDiff = (detail.items ?? []).filter((i) => (i.diff_qty ?? 0) !== 0);
        const missing = itemsWithDiff.filter((i) => !resolutions[i.id]);
        if (missing.length > 0) {
            setError(t('Sve stavke s razlikom moraju imati odluku (rezoluciju) prije odobrenja.', 'All items with a diff need a resolution before approval.'));
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await mutate(`/inventory-daily/sessions/${detail.id}/approve`, token, 'POST', {
                resolutions: itemsWithDiff.map((i) => ({ item_id: i.id, resolution: resolutions[i.id] })),
            });
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    const doPost = async () => {
        if (!detail) return;
        if (!gkReference) {
            setError(t('Unesi GK referencu.', 'Enter a GK reference.'));
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await mutate(`/inventory-daily/sessions/${detail.id}/post`, token, 'POST', { gk_reference: gkReference });
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setBusy(false);
        }
    };

    const addCause = async () => {
        if (!causeForm.code || !causeForm.label_hr || !causeForm.label_en) return;
        setSavingCause(true);
        setError(null);
        try {
            await mutate('/inventory-daily/cause-lookup', token, 'POST', causeForm);
            setCauseForm({ code: '', label_hr: '', label_en: '' });
            setShowCauseForm(false);
            await reloadCauses();
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setSavingCause(false);
        }
    };

    const uploadZapisnik = async () => {
        if (!detail || !zapisnikFile) return;
        setUploadingZapisnik(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append('file', zapisnikFile);
            await uploadFile(`/inventory-daily/sessions/${detail.id}/zapisnik`, token, fd);
            setZapisnikFile(null);
            await loadDetail(detail.id);
        } catch (e: any) {
            setError(String(e?.message ?? e));
        } finally {
            setUploadingZapisnik(false);
        }
    };

    const causeLabel = (code: string | null): string => {
        if (!code) return '—';
        const c = causes.find((x) => x.code === code);
        if (!c) return code;
        return locale === 'en' ? c.label_en : c.label_hr;
    };

    /** UID746: traka u pregledu popisa - referentni batch, rekalkulacije i gumb. */
    const renderRecalcBar = (d: SessionRow) => {
        const info = d.recalc;
        if (!info) return null;
        const btn = recalcButtonState(info);
        const last = lastRecalc(info);
        if (!last && !btn.visible && !btn.hint) return null;
        return (
            <div data-testid="recalc-bar" style={{ background: '#f4f4f4', border: '1px solid #bbb', color: '#222', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.84rem' }}>
                {last ? (
                    <div>
                        <strong>{t('Popis je rekalkuliran', 'Session was recalculated')}</strong>
                        {' - '}{t('referentni Faros batch je sada', 'reference Faros batch is now')} <strong>{info.frozen_batch_id}</strong>
                        {info.frozen_batch?.snapshot_date ? ' (' + t('stanje na', 'stock as of') + ' ' + formatDate(info.frozen_batch.snapshot_date, locale) + (info.frozen_batch.imported_at ? ', ' + t('uvezeno', 'imported') + ' ' + formatDateTime(info.frozen_batch.imported_at, locale) : '') + ')' : ''}
                        {'; '}{t('izvorni batch kod otvaranja', 'original batch at opening')}: <strong>{info.original_batch_id}</strong>.
                        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                            {info.history.map((h) => (
                                <li key={h.id}>
                                    {t('Batch', 'Batch')} {h.from_batch_id} → {h.to_batch_id} · {h.performed_by_name ?? '#' + h.performed_by} · {formatDateTime(h.performed_at, locale)} · {t('stavki', 'items')} {h.items_changed}
                                    {d.blind ? null : <> · {t('razlika EUR', 'diff EUR')} {formatMoney(h.total_diff_before_eur, locale)} → {formatMoney(h.total_diff_after_eur, locale)}</>}
                                    <div style={{ color: '#555' }}>{t('Razlog naknadnog uvoza', 'Reason for late import')}: {h.reason}</div>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
                {btn.visible ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: last ? 8 : 0 }}>
                        <span>
                            {t('Noviji Faros batch', 'Newer Faros batch')} <strong>{info.latest_batch_id}</strong> {t('mijenja knjigovodstveno stanje', 'changes book stock of')} <strong>{info.items_changed}</strong> {t('stavki ovog popisa (zamrznut batch', 'items of this session (frozen batch')} {info.frozen_batch_id}).
                        </span>
                        <button type="button" data-testid="recalc-button" disabled={loadingRecalc || busy} onClick={() => void openRecalc()} style={{ ...BTN, background: '#333', color: '#fff' }}>
                            {loadingRecalc ? '…' : t('Rekalkuliraj', 'Recalculate')}
                        </button>
                    </div>
                ) : null}
                {btn.hint ? <div style={{ color: '#555', marginTop: last ? 8 : 0 }}>{btn.hint}</div> : null}
            </div>
        );
    };

    /** UID746: sažetak prije potvrde - staro → novo po stavci i posljedica na razlike/ocjenu. */
    const renderRecalcPreview = (p: RecalcPreview) => {
        const s = p.summary;
        const c = s ? consequence(s) : null;
        const ok = canConfirm(p, recalcReason, busy);
        return (
            <div data-testid="recalc-preview" style={{ ...card, borderColor: '#999' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>{t('Rekalkulacija knjigovodstvenog stanja', 'Book stock recalculation')}: batch {p.from_batch_id} → {p.to_batch_id}</strong>
                    <button type="button" onClick={() => { setRecalcPreview(null); setRecalcReason(''); }} style={{ background: 'transparent', border: 0, color: '#888', cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                </div>
                {!p.executable || !s || !c ? (
                    <p style={{ color: '#555' }}>{p.reason ?? t('Rekalkulacija nije moguća.', 'Recalculation is not possible.')}</p>
                ) : (
                    <>
                        <p style={{ color: '#555', fontSize: '0.8rem', margin: '4px 0 10px' }}>
                            {t('Mijenja se samo knjigovodstveno stanje i razlike koje iz njega proizlaze. Izbrojane količine, uzroci i napomene ostaju kakvi jesu. Izvorno stanje ostaje zapisano.', 'Only book stock and the differences derived from it change. Counted quantities, causes and notes stay as they are. The original stock stays on record.')}
                            {s.to_batch?.snapshot_date ? ' ' + t('Novi batch: stanje na', 'New batch: stock as of') + ' ' + formatDate(s.to_batch.snapshot_date, locale) + (s.to_batch.imported_at ? ', ' + t('uvezeno', 'imported') + ' ' + formatDateTime(s.to_batch.imported_at, locale) : '') + (s.to_batch.filename ? ' (' + s.to_batch.filename + ')' : '') + '.' : ''}
                        </p>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.82rem', marginBottom: 8 }}>
                            <span>{t('Pogođenih stavki', 'Items affected')}: <strong data-testid="recalc-items">{c.itemsChanged}</strong></span>
                            <span>{t('Neto razlika EUR', 'Net diff EUR')}: <strong>{formatMoney(c.totalBefore, locale)} → {formatMoney(c.totalAfter, locale)}</strong></span>
                            <span>{t('Na ponovno brojanje', 'To recount')}: <strong>{c.recountBefore} → {c.recountAfter}</strong></span>
                        </div>
                        <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse', marginBottom: 10 }}>
                            <thead>
                                <tr style={{ background: '#eee' }}>
                                    <th style={{ padding: 6, textAlign: 'left' }}>{t('Šifra', 'Code')}</th>
                                    <th style={{ textAlign: 'left' }}>{t('Naziv', 'Name')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Knjig. staro → novo', 'Book old → new')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Izbrojano', 'Counted')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Razlika', 'Diff')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Razlika EUR', 'Diff EUR')}</th>
                                    <th style={{ textAlign: 'left' }}>{t('Ocjena', 'Status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {s.rows.map((r) => (
                                    <tr key={r.item_id} data-testid={'recalc-row-' + r.item_code} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: 6 }}>{r.item_code}</td>
                                        <td>{r.item_name ?? '-'}{r.in_new_batch ? null : <span style={{ color: '#555' }}> ({t('nema ga u novom batchu', 'not in new batch')})</span>}</td>
                                        <td style={{ textAlign: 'right' }}>{arrow(r.old_expected_qty, r.new_expected_qty)}</td>
                                        <td style={{ textAlign: 'right' }}>{fmtQty(r.counted_qty)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{arrow(r.old_diff_qty, r.new_diff_qty)}</td>
                                        <td style={{ textAlign: 'right' }}>{r.old_diff_value_eur == null ? '-' : formatMoney(r.old_diff_value_eur, locale)} → {r.new_diff_value_eur == null ? '-' : formatMoney(r.new_diff_value_eur, locale)}</td>
                                        <td>{r.old_status && r.new_status ? RECON_STATUS(r.old_status as any, locale).label + ' → ' + RECON_STATUS(r.new_status as any, locale).label : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <strong style={{ fontSize: '0.85rem' }}>{t('Razlog naknadnog uvoza (obavezno)', 'Reason for the late import (required)')}</strong>
                        <textarea
                            data-testid="recalc-reason"
                            value={recalcReason}
                            onChange={(e) => setRecalcReason(e.target.value)}
                            rows={2}
                            placeholder={t('npr. Faros proknjižio izlaze 25.-28.09. tek u izvozu 28.09. 14:00', 'e.g. Faros posted issues of 25-28 Sep only in the 28 Sep 14:00 export')}
                            style={{ ...INP, width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ color: '#666', fontSize: '0.78rem' }}>{t('Najmanje', 'At least')} {MIN_REASON_LENGTH} {t('znakova. Zapisuje se tko je i kada rekalkulirao, stari i novi batch te stare i nove vrijednosti.', 'characters. Who and when, old and new batch, and old and new values are recorded.')}</span>
                            <button type="button" data-testid="recalc-confirm" disabled={!ok} onClick={() => void doRecalc()} style={{ ...BTN, background: '#333', color: '#fff', opacity: ok ? 1 : 0.5 }}>
                                {busy ? '…' : t('Potvrdi rekalkulaciju', 'Confirm recalculation')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    /**
     * Ekran rekonsilijacije prije predaje: tri stupca (knjig. stanje, prebrojano, razlika),
     * napomena po stavci gdje se stanje pomaklo ili artikla nema u stanju, i obavezno
     * obrazloženje predaje. Ovo je jedino mjesto na kojem brojač vidi stanje prije predaje.
     */
    const renderPreview = (p: Preview) => {
        const tot = p.totals;
        const needNote = p.rows.filter((r) => r.item_id !== null && r.needs_note);
        const needCause = p.rows.filter((r) => r.item_id !== null && r.needs_cause && !r.cause_code);
        const noteTooShort = (tot.items_with_diff > 0 || tot.items_drifted > 0) && submitNote.trim().length < p.min_note_length;
        const notesMissing = needNote.filter((r) => (driftNotes[r.item_id as number] ?? '').trim().length < 3);
        const canSubmit = !busy && needCause.length === 0 && notesMissing.length === 0 && !noteTooShort;

        return (
            <div style={{ ...card, borderColor: '#b8cdb8' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <strong style={{ color: '#2f4d2f' }}>{t('Usporedba sa stanjem u BIS-u', 'Comparison with BIS stock')}</strong>
                    <button type="button" onClick={() => setPreview(null)} style={{ background: 'transparent', border: 0, color: '#888', cursor: 'pointer' }}>{t('Zatvori', 'Close')}</button>
                </div>
                <p style={{ color: '#888', fontSize: '0.78rem', margin: '4px 0 10px' }}>
                    {t('Referentno stanje je zamrznuto u trenutku otvaranja popisa (Faros batch', 'The reference stock was frozen when the session was opened (Faros batch')} {tot.frozen_batch_id ?? '—'}
                    {tot.current_batch_id !== tot.frozen_batch_id ? t('), a trenutno stanje je batch ', '), current stock is batch ') + (tot.current_batch_id ?? '—') + ').' : ').'}
                    {' '}{t('Prag ponovnog brojanja:', 'Recount threshold:')} {fmtQty(tot.thresholds.qty_abs)} {t('kom', 'pcs')} / {fmtQty(tot.thresholds.qty_pct)}% / {formatMoney(tot.thresholds.value_eur, locale)}.
                </p>

                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10, fontSize: '0.82rem' }}>
                    <span>{t('Stavki', 'Items')}: <strong>{tot.items_total}</strong></span>
                    <span>{t('S razlikom', 'With difference')}: <strong style={{ color: tot.items_with_diff > 0 ? '#b98b1f' : '#333' }}>{tot.items_with_diff}</strong></span>
                    <span>{t('Na ponovno brojanje', 'To recount')}: <strong style={{ color: tot.items_recount > 0 ? '#b1602f' : '#333' }}>{tot.items_recount}</strong></span>
                    <span>{t('Pomak stanja', 'Stock drift')}: <strong style={{ color: tot.items_drifted > 0 ? '#b1602f' : '#333' }}>{tot.items_drifted}</strong></span>
                    <span>{t('Nije popisano', 'Not counted')}: <strong>{tot.items_missing}</strong></span>
                    <span>{t('Višak', 'Surplus')}: <strong style={{ color: '#1f7a36' }}>{formatMoney(tot.surplus_value_eur, locale)}</strong></span>
                    <span>{t('Manjak', 'Shortage')}: <strong style={{ color: '#b14040' }}>{formatMoney(tot.shortage_value_eur, locale)}</strong></span>
                    <span>{t('Neto', 'Net')}: <strong>{formatMoney(tot.total_diff_value_eur, locale)}</strong></span>
                </div>

                <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse', marginBottom: 12 }}>
                    <thead>
                        <tr style={TH}>
                            <th style={{ padding: 6, textAlign: 'left' }}>{t('Šifra', 'Code')}</th>
                            <th style={{ textAlign: 'left' }}>{t('Naziv', 'Name')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Knjig. stanje', 'Book qty')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Prebrojano', 'Counted')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Razlika', 'Diff')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Razlika EUR', 'Diff EUR')}</th>
                            <th style={{ textAlign: 'left' }}>{t('Ocjena', 'Status')}</th>
                            <th style={{ textAlign: 'left' }}>{t('Uzrok', 'Cause')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {p.rows.map((r, idx) => {
                            const st = RECON_STATUS(r.status, locale);
                            return (
                                <tr key={r.item_id ?? 'x' + idx} style={{ borderBottom: '1px solid #eef4ee' }}>
                                    <td style={{ padding: 6 }}>{r.item_code}</td>
                                    <td>{r.item_name ?? '—'}</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {fmtQty(r.expected_qty)}
                                        {r.drift_qty !== 0 ? <span title={t('Stanje se promijenilo tijekom popisa', 'Stock changed during the count')} style={{ color: '#b1602f', fontSize: '0.72rem' }}> ({r.drift_qty > 0 ? '+' : ''}{fmtQty(r.drift_qty)})</span> : null}
                                        {r.item_id !== null && originalFor(detail?.recalc, r.item_id) ? <span title={t('Izvorno knjig. stanje prije rekalkulacije', 'Original book qty before recalculation')} style={{ color: '#555', fontSize: '0.72rem' }}> [{t('izvorno', 'orig.')} {fmtQty(originalFor(detail?.recalc, r.item_id)?.original_expected_qty)}]</span> : null}
                                        {r.entered_expected_qty != null && r.entered_expected_qty !== r.expected_qty ? <span title={t('Ručno upisano knjig. stanje', 'Manually entered book qty')} style={{ color: '#888', fontSize: '0.72rem' }}> [{fmtQty(r.entered_expected_qty)}]</span> : null}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {fmtQty(r.counted_qty)}
                                        {r.recount_qty != null ? <span style={{ color: '#888', fontSize: '0.72rem' }}> ({t('1.', '1st')} {fmtQty(r.first_counted_qty)})</span> : null}
                                    </td>
                                    <td style={{ textAlign: 'right', fontWeight: 600, color: (r.diff_qty ?? 0) < 0 ? '#b14040' : (r.diff_qty ?? 0) > 0 ? '#1f7a36' : '#333' }}>{fmtQty(r.diff_qty)}</td>
                                    <td style={{ textAlign: 'right', color: (r.diff_value_eur ?? 0) < 0 ? '#b14040' : (r.diff_value_eur ?? 0) > 0 ? '#1f7a36' : '#333' }}>{r.diff_value_eur == null ? '—' : formatMoney(r.diff_value_eur, locale)}</td>
                                    <td><span style={{ color: st.color, fontWeight: 600 }}>{st.label}</span></td>
                                    <td style={{ color: r.needs_cause && !r.cause_code ? '#b14040' : '#333' }}>{r.needs_cause && !r.cause_code ? t('nedostaje', 'missing') : causeLabel(r.cause_code)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {needCause.length > 0 ? (
                    <div style={{ background: '#fbeaea', border: '1px solid #e3b0b0', color: '#8a3a3a', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.82rem' }}>
                        {t('Uzrok nedostaje na stavkama:', 'Cause is missing on items:')} {needCause.map((r) => r.item_code).join(', ')}. {t('Uzrok se upisuje kroz uređivanje stavke.', 'Set it by editing the item.')}
                    </div>
                ) : null}

                {needNote.length > 0 ? (
                    <div style={{ marginBottom: 12 }}>
                        <strong style={{ color: '#8a4a1f', fontSize: '0.88rem' }}>{t('Napomene po stavci (obavezno)', 'Per-item notes (required)')}</strong>
                        {needNote.map((r) => (
                            <div key={r.item_id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                                <span style={{ minWidth: 260, fontSize: '0.82rem' }}>
                                    {r.item_code} · {r.status === 'unknown'
                                        ? t('artikla nema u knjigovodstvenom stanju', 'item is not in book stock')
                                        : t('stanje se promijenilo za', 'stock changed by') + ' ' + fmtQty(r.drift_qty)}
                                </span>
                                <input
                                    placeholder={t('Obrazloženje *', 'Explanation *')}
                                    value={driftNotes[r.item_id as number] ?? ''}
                                    onChange={(e) => setDriftNotes({ ...driftNotes, [r.item_id as number]: e.target.value })}
                                    style={{ ...INP, width: 420, borderColor: (driftNotes[r.item_id as number] ?? '').trim().length < 3 ? '#e3b0b0' : '#ccc' }}
                                />
                            </div>
                        ))}
                    </div>
                ) : null}

                <div style={{ borderTop: '1px solid #e0ecdf', paddingTop: 12 }}>
                    <strong style={{ color: '#2f4d2f', fontSize: '0.88rem' }}>{t('Obrazloženje predaje', 'Submission note')}</strong>
                    <div style={{ color: '#888', fontSize: '0.78rem', marginBottom: 6 }}>
                        {t('Ide u zapisnik. Obavezno kad ima razlika ili pomaka stanja, najmanje', 'Goes into the report. Required when there are differences or drift, at least')} {p.min_note_length} {t('znakova.', 'characters.')}
                    </div>
                    <textarea
                        value={submitNote}
                        onChange={(e) => setSubmitNote(e.target.value)}
                        rows={3}
                        style={{ ...INP, width: '100%', boxSizing: 'border-box', borderColor: noteTooShort ? '#e3b0b0' : '#ccc' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ color: '#888', fontSize: '0.78rem' }}>
                            {tot.items_recount > 0
                                ? t('Nakon predaje popis ide u ponovno brojanje: stavki iznad tolerancije je', 'After submission the session goes to recount: items above tolerance:') + ' ' + tot.items_recount + '.'
                                : t('Nakon predaje generira se zapisnik i popis ide na pregled.', 'After submission a report is generated and the session goes to review.')}
                        </span>
                        <button type="button" disabled={!canSubmit} onClick={() => void doSubmit()} style={{ ...BTN, opacity: canSubmit ? 1 : 0.5 }}>
                            {busy ? '…' : '✓ ' + t('Predaj popis', 'Submit session')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const errorBanner = error ? (
        <div style={{ background: '#fbeaea', border: '1px solid #e3b0b0', color: '#8a3a3a', borderRadius: 8, padding: '10px 14px', marginTop: 4, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ background: 'transparent', border: 0, color: '#8a3a3a', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' }}>×</button>
        </div>
    ) : null;

    return (
        <section style={{ maxWidth: 1100, margin: '0 auto' }}>
            {onBack ? <Breadcrumb items={[{ label: t('40 · Skladišno poslovanje', '40 · Warehouse'), onClick: onBack }, { label: '40.7 · ' + t('Dnevna inventura', 'Daily inventory count') }]} /> : null}
            <div className="bi-module-hero" style={{ marginTop: onBack ? '0.5rem' : 0 }}>
                <div className="bi-module-hero-icon">📋</div>
                <div className="bi-module-hero-text">
                    <h1>{t('Dnevna inventura', 'Daily inventory count')}</h1>
                    <p className="muted">{t('Popis viškova/manjkova po skladištu — unos, pregled, odobrenje, knjiženje.', 'Warehouse surplus/shortage count — entry, review, approval, posting.')}</p>
                </div>
            </div>

            {errorBanner}

            {view === 'list' ? (
                <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={INP}>
                            <option value="">{t('Svi statusi', 'All statuses')}</option>
                            {(['draft', 'recount', 'submitted', 'reviewed', 'approved', 'posted'] as SessionStatus[]).map((s) => (
                                <option key={s} value={s}>{STATUS_LABEL(s, locale)}</option>
                            ))}
                        </select>
                        <select value={filterWarehouse} onChange={(e) => setFilterWarehouse(e.target.value)} style={INP}>
                            <option value="">{t('Sva skladišta', 'All warehouses')}</option>
                            {warehouses.map((w) => (
                                <option key={w.code} value={w.code}>{w.code} · {w.name}</option>
                            ))}
                        </select>
                        <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={INP} />
                        <span style={{ color: '#888' }}>–</span>
                        <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={INP} />
                        <span style={{ flex: 1 }} />
                        <button type="button" onClick={() => setShowNewForm((s) => !s)} style={BTN}>＋ {t('Novi popis', 'New session')}</button>
                    </div>

                    {showNewForm ? (
                        <div style={{ ...card, background: '#f4f9f4' }}>
                            <strong style={{ color: '#2f4d2f' }}>{t('Novi popis inventure', 'New inventory session')}</strong>
                            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <select value={newForm.warehouse_code} onChange={(e) => setNewForm({ ...newForm, warehouse_code: e.target.value })} style={INP}>
                                    <option value="">{t('Skladište *', 'Warehouse *')}</option>
                                    {warehouses.map((w) => (
                                        <option key={w.code} value={w.code}>{w.code} · {w.name}{w.inventory_lock ? ' · ' + t('INVENTURA', 'STOCKTAKE') : ''}</option>
                                    ))}
                                </select>
                                <select value={newForm.inventory_type} onChange={(e) => setNewForm({ ...newForm, inventory_type: e.target.value as InventoryType })} style={INP}>
                                    <option value="operativna">{t('Operativna', 'Operational')}</option>
                                    <option value="godisnja">{t('Godišnja', 'Annual')}</option>
                                    <option value="izvanredna">{t('Izvanredna', 'Extraordinary')}</option>
                                </select>
                                <input type="date" value={newForm.inventory_date} onChange={(e) => setNewForm({ ...newForm, inventory_date: e.target.value })} style={INP} />
                                <input placeholder={t('Napomena komisije', 'Committee note')} value={newForm.committee_note} onChange={(e) => setNewForm({ ...newForm, committee_note: e.target.value })} style={{ ...INP, width: 260 }} />
                                <button type="button" disabled={creating} onClick={() => void createSession()} style={BTN}>{creating ? '…' : '✓ ' + t('Otvori popis', 'Open session')}</button>
                                <button type="button" onClick={() => setShowNewForm(false)} style={{ background: 'transparent', border: 0, color: '#888', cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                            </div>
                            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: '0.85rem', color: '#2f4d2f', cursor: 'pointer' }}>
                                <input type="checkbox" checked={newForm.lock_warehouse} onChange={(e) => setNewForm({ ...newForm, lock_warehouse: e.target.checked })} />
                                <span>{t('Status INVENTURA na skladištu', 'Set STOCKTAKE status on warehouse')}</span>
                            </label>
                            <p style={{ color: '#888', fontSize: '0.78rem', margin: '4px 0 0 26px' }}>
                                {t('Dok je status uključen, knjigovodstveno stanje tog skladišta vidi samo superadministrator. Status se skida automatski kod knjiženja popisa, ili ručno u detalju popisa.', 'While set, only a superadministrator can see the book stock for that warehouse. It is released automatically when the session is posted, or manually in the session detail.')}
                            </p>
                        </div>
                    ) : null}

                    {loadingList ? (
                        <BrandSpinner label={t('Učitavam…', 'Loading…')} />
                    ) : (
                        <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={TH}>
                                    <th style={{ padding: 6, textAlign: 'left' }}>{t('Skladište', 'Warehouse')}</th>
                                    <th style={{ textAlign: 'left' }}>{t('Tip', 'Type')}</th>
                                    <th style={{ textAlign: 'left' }}>{t('Datum', 'Date')}</th>
                                    <th style={{ textAlign: 'left' }}>{t('Status', 'Status')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Razlika EUR', 'Diff EUR')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sessions.map((s) => (
                                    <tr key={s.id} style={{ borderBottom: '1px solid #eef4ee', cursor: 'pointer' }} onClick={() => openDetail(s.id)}>
                                        <td style={{ padding: 6 }}>{s.warehouse_code} · {s.warehouse_name ?? '—'}</td>
                                        <td>{TYPE_LABEL(s.inventory_type, locale)}</td>
                                        <td>{formatDate(s.inventory_date, locale)}</td>
                                        <td><span style={{ background: STATUS_COLOR[s.status], color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700 }}>{STATUS_LABEL(s.status, locale)}</span></td>
                                        <td style={{ textAlign: 'right', fontWeight: 600, color: (s.total_diff_value_eur ?? 0) < 0 ? '#b14040' : (s.total_diff_value_eur ?? 0) > 0 ? '#1f7a36' : '#333' }}>{s.total_diff_value_eur == null ? '—' : formatMoney(s.total_diff_value_eur, locale)}</td>
                                        <td style={{ textAlign: 'right' }}><button type="button" onClick={(e) => { e.stopPropagation(); openDetail(s.id); }} style={{ ...BTN, padding: '3px 10px', fontSize: '0.75rem' }}>{t('Otvori', 'Open')} →</button></td>
                                    </tr>
                                ))}
                                {sessions.length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: '#aaa', textAlign: 'center' }}>{t('Nema popisa.', 'No sessions.')}</td></tr>}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : (
                <div>
                    <button type="button" onClick={backToList} style={{ background: 'transparent', border: 0, color: '#2f4d2f', cursor: 'pointer', marginBottom: 10, fontWeight: 600 }}>← {t('Natrag na popis', 'Back to list')}</button>

                    {loadingDetail || !detail ? (
                        <BrandSpinner label={t('Učitavam…', 'Loading…')} />
                    ) : (
                        <div>
                            <div style={{ ...card, background: '#f4f9f4', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                <div>
                                    <strong style={{ color: '#2f4d2f', fontSize: '1.05rem' }}>{detail.warehouse_code} · {detail.warehouse_name ?? '—'}</strong>
                                    <div style={{ color: '#888', fontSize: '0.82rem' }}>{TYPE_LABEL(detail.inventory_type, locale)} · {formatDate(detail.inventory_date, locale)}</div>
                                    {detail.committee_note ? <div style={{ color: '#666', fontSize: '0.82rem', marginTop: 4 }}>{t('Napomena', 'Note')}: {detail.committee_note}</div> : null}
                                    {detail.review_note ? <div style={{ color: '#8a6a1f', fontSize: '0.82rem', marginTop: 4 }}>{t('Napomena pregleda', 'Review note')}: {detail.review_note}</div> : null}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <span style={{ background: STATUS_COLOR[detail.status], color: '#fff', borderRadius: 12, padding: '3px 12px', fontWeight: 700 }}>{STATUS_LABEL(detail.status, locale)}</span>
                                    {detail.warehouse_locked ? <span style={{ background: '#8a3a3a', color: '#fff', borderRadius: 12, padding: '3px 12px', fontWeight: 700, marginLeft: 6 }}>{t('INVENTURA', 'STOCKTAKE')}</span> : null}
                                    <div style={{ marginTop: 6, fontWeight: 700, fontSize: '1.1rem', color: (detail.total_diff_value_eur ?? 0) < 0 ? '#b14040' : (detail.total_diff_value_eur ?? 0) > 0 ? '#1f7a36' : '#333' }}>
                                        {detail.total_diff_value_eur == null ? <span style={{ color: '#888', fontSize: '0.82rem', fontWeight: 500 }}>{t('Razlika skrivena do predaje', 'Difference hidden until submit')}</span> : formatMoney(detail.total_diff_value_eur, locale)}
                                    </div>
                                    {detail.gk_reference ? <div style={{ color: '#888', fontSize: '0.78rem' }}>GK: {detail.gk_reference}</div> : null}
                                    <button type="button" onClick={() => void toggleWarehouseLock(detail.warehouse_code, !detail.warehouse_locked)} style={{ background: 'transparent', border: 0, color: '#4f9bb6', cursor: 'pointer', fontSize: '0.78rem', marginTop: 4 }}>
                                        {detail.warehouse_locked ? t('Skini status INVENTURA', 'Release STOCKTAKE status') : t('Postavi status INVENTURA', 'Set STOCKTAKE status')}
                                    </button>
                                </div>
                            </div>

                            {detail.blind ? (
                                <div style={{ background: '#fdf6e6', border: '1px solid #e6d4a8', color: '#7a5c14', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem' }}>
                                    {t('Slijepo brojanje: knjigovodstveno stanje nije vidljivo dok se popis ne preda. Usporedba, razlike i zapisnik dolaze na ekranu prije predaje.', 'Blind count: book stock stays hidden until the session is submitted. Comparison, differences and the report appear on the pre-submit screen.')}
                                </div>
                            ) : null}

                            {renderRecalcBar(detail)}
                            {recalcPreview ? renderRecalcPreview(recalcPreview) : null}

                            <strong style={{ color: '#2f4d2f' }}>{t('Stavke', 'Items')}</strong>
                            <table style={{ width: '100%', marginTop: 6, marginBottom: 16, fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={TH}>
                                        <th style={{ padding: 6, textAlign: 'left' }}>{t('Šifra', 'Code')}</th>
                                        <th style={{ textAlign: 'left' }}>{t('Naziv', 'Name')}</th>
                                        {detail.blind ? null : <th style={{ textAlign: 'right' }}>{t('Knjig.', 'Book')}</th>}
                                        <th style={{ textAlign: 'right' }}>{t('Izbrojano', 'Counted')}</th>
                                        {detail.blind ? null : <th style={{ textAlign: 'right' }}>{t('Razlika', 'Diff')}</th>}
                                        {detail.blind ? null : <th style={{ textAlign: 'right' }}>{t('Razlika EUR', 'Diff EUR')}</th>}
                                        <th style={{ textAlign: 'left' }}>{t('Uzrok', 'Cause')}</th>
                                        <th style={{ textAlign: 'left' }}>{t('Napomena', 'Note')}</th>
                                        {detail.blind ? null : <th style={{ textAlign: 'left' }}>{t('Rezolucija', 'Resolution')}</th>}
                                        {detail.status === 'draft' ? <th></th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(detail.items ?? []).map((i) => (
                                        <tr key={i.id} style={{ borderBottom: '1px solid #eef4ee' }}>
                                            <td style={{ padding: 6 }}>{i.item_code}{i.recount_required ? <span title={t('Ide na ponovno brojanje', 'Flagged for recount')} style={{ color: '#b1602f', fontWeight: 700 }}> ↻</span> : null}</td>
                                            <td>{i.item_name ?? '—'}</td>
                                            {detail.blind ? null : <td style={{ textAlign: 'right' }}>{fmtQty(i.expected_qty)} {i.expected_source !== 'manual' ? <span title={t('Iz Faros snapshota', 'From Faros snapshot')} style={{ color: '#4f9bb6', fontSize: '0.7rem' }}>●</span> : null}{originalFor(detail.recalc, i.id) ? <div data-testid={'orig-' + i.item_code} style={{ color: '#555', fontSize: '0.72rem' }}>{t('izvorno', 'original')} {fmtQty(originalFor(detail.recalc, i.id)?.original_expected_qty)} (batch {originalFor(detail.recalc, i.id)?.original_batch_id})</div> : null}</td>}
                                            <td style={{ textAlign: 'right' }}>{fmtQty(i.counted_qty)}{i.recount_qty != null ? <span style={{ color: '#888', fontSize: '0.75rem' }}> ({t('1. brojanje', '1st count')}: {fmtQty(i.first_counted_qty)})</span> : null}</td>
                                            {detail.blind ? null : <td style={{ textAlign: 'right', fontWeight: 600, color: (i.diff_qty ?? 0) < 0 ? '#b14040' : (i.diff_qty ?? 0) > 0 ? '#1f7a36' : '#333' }}>{fmtQty(i.diff_qty)}</td>}
                                            {detail.blind ? null : <td style={{ textAlign: 'right', color: (i.diff_value_eur ?? 0) < 0 ? '#b14040' : (i.diff_value_eur ?? 0) > 0 ? '#1f7a36' : '#333' }}>{formatMoney(i.diff_value_eur ?? 0, locale)}</td>}
                                            <td>{causeLabel(i.cause_code)}</td>
                                            <td>{i.cause_note ?? ''}</td>
                                            {detail.blind ? null : <td>{RESOLUTION_LABEL(i.resolution ?? null, locale)}</td>}
                                            {detail.status === 'draft' ? (
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <button type="button" onClick={() => editItem(i)} style={{ background: 'transparent', border: 0, color: '#2f4d2f', cursor: 'pointer' }}>✎</button>
                                                    <button type="button" onClick={() => void deleteItem(i.id)} style={{ background: 'transparent', border: 0, color: '#b14040', cursor: 'pointer' }}>×</button>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                    {(detail.items ?? []).length === 0 && <tr><td colSpan={10} style={{ padding: 14, color: '#aaa', textAlign: 'center' }}>{t('Nema unesenih stavki.', 'No items yet.')}</td></tr>}
                                </tbody>
                            </table>

                            {detail.status === 'draft' ? (
                                <div style={card}>
                                    <strong style={{ color: '#2f4d2f' }}>{t('Dodaj / ažuriraj stavku', 'Add / update item')}</strong>
                                    <p style={{ color: '#888', fontSize: '0.78rem', margin: '4px 0 8px' }}>{t('Unos je upsert po šifri artikla — ponovni unos iste šifre ažurira postojeću stavku. Knjigovodstveno stanje se ne upisuje: referentna vrijednost je stanje zamrznuto u trenutku otvaranja popisa.', 'Entry upserts by item code — re-entering the same code updates the existing item. Book qty is not entered: the reference is the stock frozen when the session was opened.')}</p>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <input placeholder={t('Šifra artikla *', 'Item code *')} value={itemForm.item_code} onChange={(e) => setItemForm({ ...itemForm, item_code: e.target.value })} style={{ ...INP, width: 130 }} />
                                        <input placeholder={t('Naziv', 'Name')} value={itemForm.item_name} onChange={(e) => setItemForm({ ...itemForm, item_name: e.target.value })} style={{ ...INP, width: 200 }} />
                                        <input placeholder={t('Izbrojano *', 'Counted *')} type="number" step="0.001" value={itemForm.counted_qty} onChange={(e) => setItemForm({ ...itemForm, counted_qty: e.target.value })} style={{ ...INP, width: 110 }} />
                                        <input placeholder={t('Cijena EUR', 'Unit price EUR')} type="number" step="0.01" value={itemForm.unit_price_eur} onChange={(e) => setItemForm({ ...itemForm, unit_price_eur: e.target.value })} style={{ ...INP, width: 110 }} />
                                        <select value={itemForm.cause_code} onChange={(e) => setItemForm({ ...itemForm, cause_code: e.target.value })} style={INP}>
                                            <option value="">{t('Uzrok', 'Cause')}</option>
                                            {causes.map((c) => (
                                                <option key={c.code} value={c.code}>{locale === 'en' ? c.label_en : c.label_hr}</option>
                                            ))}
                                        </select>
                                        <input placeholder={t('Napomena', 'Note')} value={itemForm.cause_note} onChange={(e) => setItemForm({ ...itemForm, cause_note: e.target.value })} style={{ ...INP, width: 180 }} />
                                        <button type="button" disabled={savingItem} onClick={() => void saveItem()} style={BTN}>{savingItem ? '…' : '✓ ' + t('Spremi stavku', 'Save item')}</button>
                                        {itemForm.item_code ? <button type="button" onClick={() => setItemForm(emptyItemForm)} style={{ background: 'transparent', border: 0, color: '#888', cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button> : null}
                                    </div>

                                    <div style={{ marginTop: 10 }}>
                                        {!showCauseForm ? (
                                            <button type="button" onClick={() => setShowCauseForm(true)} style={{ background: 'transparent', border: 0, color: '#4f9bb6', cursor: 'pointer', fontSize: '0.8rem' }}>＋ {t('Novi uzrok u šifrarniku', 'New cause code')}</button>
                                        ) : (
                                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                                                <input placeholder={t('Šifra', 'Code')} value={causeForm.code} onChange={(e) => setCauseForm({ ...causeForm, code: e.target.value })} style={{ ...INP, width: 120 }} />
                                                <input placeholder={t('Naziv HR', 'Label HR')} value={causeForm.label_hr} onChange={(e) => setCauseForm({ ...causeForm, label_hr: e.target.value })} style={{ ...INP, width: 180 }} />
                                                <input placeholder={t('Naziv EN', 'Label EN')} value={causeForm.label_en} onChange={(e) => setCauseForm({ ...causeForm, label_en: e.target.value })} style={{ ...INP, width: 180 }} />
                                                <button type="button" disabled={savingCause} onClick={() => void addCause()} style={{ ...BTN, padding: '5px 12px' }}>{savingCause ? '…' : '✓ ' + t('Dodaj', 'Add')}</button>
                                                <button type="button" onClick={() => setShowCauseForm(false)} style={{ background: 'transparent', border: 0, color: '#888', cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ textAlign: 'right', marginTop: 14, borderTop: '1px solid #e0ecdf', paddingTop: 12 }}>
                                        <button type="button" disabled={loadingPreview || (detail.items ?? []).length === 0} onClick={() => void openPreview()} style={{ ...BTN, opacity: (detail.items ?? []).length === 0 ? 0.5 : 1 }}>
                                            {loadingPreview ? '…' : '⇄ ' + t('Usporedi sa stanjem i predaj', 'Compare with stock and submit')}
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {preview && (detail.status === 'draft' || detail.status === 'recount') ? renderPreview(preview) : null}

                            {detail.status === 'recount' ? (
                                <div style={{ ...card, borderColor: '#e0c0a8', background: '#fdf3ec' }}>
                                    <strong style={{ color: '#8a4a1f' }}>{t('Ponovno brojanje', 'Recount')}</strong>
                                    <p style={{ color: '#8a6a4a', fontSize: '0.8rem', margin: '4px 0 10px' }}>
                                        {t('Stavke ispod prelaze toleranciju. Ponovno brojanje mora obaviti druga osoba - onaj tko je prvi put brojao ne smije potvrditi vlastiti rezultat. Prvo brojanje ostaje zapisano.', 'The items below exceed tolerance. The recount must be done by a second person - whoever counted first may not confirm their own result. The first count stays on record.')}
                                    </p>
                                    {(detail.items ?? []).filter((i) => i.recount_required && i.recount_qty == null).map((i) => (
                                        <div key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                                            <span style={{ minWidth: 300 }}>{i.item_code} · {i.item_name ?? '—'}</span>
                                            <input placeholder={t('Ponovno prebrojano *', 'Recounted qty *')} type="number" step="0.001" value={recountQty[i.id] ?? ''} onChange={(e) => setRecountQty({ ...recountQty, [i.id]: e.target.value })} style={{ ...INP, width: 150 }} />
                                            <button type="button" disabled={busy} onClick={() => void doRecount(i.id)} style={{ ...BTN, background: '#8a4a1f' }}>{busy ? '…' : '✓ ' + t('Potvrdi', 'Confirm')}</button>
                                        </div>
                                    ))}
                                    {(detail.items ?? []).filter((i) => i.recount_required && i.recount_qty == null).length === 0 ? (
                                        <div style={{ marginTop: 6 }}>
                                            <div style={{ color: '#1f7a36', marginBottom: 8 }}>{t('Sva ponovna brojanja su obavljena. Popis se može ponovno predati.', 'All recounts are done. The session can be submitted again.')}</div>
                                            <button type="button" disabled={loadingPreview} onClick={() => void openPreview()} style={BTN}>{loadingPreview ? '…' : '⇄ ' + t('Usporedi sa stanjem i predaj', 'Compare with stock and submit')}</button>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            {detail.status === 'submitted' ? (
                                <div style={card}>
                                    <strong style={{ color: '#2f4d2f' }}>{t('Pregled (manager+)', 'Review (manager+)')}</strong>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <input placeholder={t('Napomena (opcionalno)', 'Note (optional)')} value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} style={{ ...INP, width: 300 }} />
                                        <button type="button" disabled={busy} onClick={() => void doReview('accept')} style={BTN}>{busy ? '…' : '✓ ' + t('Prihvati', 'Accept')}</button>
                                        <button type="button" disabled={busy} onClick={() => void doReview('reject')} style={{ ...BTN, background: '#b14040' }}>{busy ? '…' : '↩ ' + t('Vrati na doradu', 'Send back')}</button>
                                    </div>
                                </div>
                            ) : null}

                            {detail.status === 'reviewed' ? (
                                <div style={card}>
                                    <strong style={{ color: '#2f4d2f' }}>{t('Odluka o postupanju s razlikama', 'Resolution for differences')}</strong>
                                    <p style={{ color: '#888', fontSize: '0.78rem', margin: '4px 0 8px' }}>{t('Iznad praga ukupne razlike smije odobriti samo administrator.', 'Above the total-diff threshold only an administrator may approve.')}</p>
                                    {(detail.items ?? []).filter((i) => (i.diff_qty ?? 0) !== 0).map((i) => (
                                        <div key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                                            <span style={{ minWidth: 260 }}>{i.item_code} · {i.item_name ?? '—'} ({fmtQty(i.diff_qty)} / {formatMoney(i.diff_value_eur ?? 0, locale)})</span>
                                            <select value={resolutions[i.id] ?? ''} onChange={(e) => setResolutions({ ...resolutions, [i.id]: e.target.value as Resolution })} style={INP}>
                                                <option value="">{t('— odaberi —', '— select —')}</option>
                                                <option value="otpis">{t('Otpis', 'Write-off')}</option>
                                                <option value="teret_odgovorne_osobe">{t('Teret odgovorne osobe', 'Charged to responsible person')}</option>
                                                <option value="teret_drustva">{t('Teret društva', 'Charged to company')}</option>
                                            </select>
                                        </div>
                                    ))}
                                    {(detail.items ?? []).filter((i) => i.diff_qty !== 0).length === 0 && <p style={{ color: '#888' }}>{t('Nema stavki s razlikom.', 'No items with a difference.')}</p>}
                                    <div style={{ textAlign: 'right', marginTop: 10 }}>
                                        <button type="button" disabled={busy} onClick={() => void doApprove()} style={BTN}>{busy ? '…' : '✓ ' + t('Odobri', 'Approve')}</button>
                                    </div>
                                </div>
                            ) : null}

                            {detail.status === 'approved' ? (
                                <div style={card}>
                                    <strong style={{ color: '#2f4d2f' }}>{t('Knjiženje (administrator+)', 'Posting (administrator+)')}</strong>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                                        <input placeholder={t('GK referenca *', 'GK reference *')} value={gkReference} onChange={(e) => setGkReference(e.target.value)} style={{ ...INP, width: 220 }} />
                                        <button type="button" disabled={busy} onClick={() => void doPost()} style={BTN}>{busy ? '…' : '✓ ' + t('Knjiži', 'Post')}</button>
                                    </div>
                                </div>
                            ) : null}

                            {detail.status === 'posted' ? (
                                <div style={{ ...card, background: '#f4f9f4' }}>
                                    <strong style={{ color: '#1f7a36' }}>✓ {t('Knjiženo', 'Posted')}</strong>
                                    <div style={{ color: '#666', marginTop: 4 }}>{t('GK referenca', 'GK reference')}: {detail.gk_reference}</div>
                                    <div style={{ color: '#888', fontSize: '0.8rem' }}>{detail.posted_at ? formatDateTime(detail.posted_at, locale) : ''}</div>
                                </div>
                            ) : null}

                            <div style={card}>
                                <strong style={{ color: '#2f4d2f' }}>{t('Zapisnik o popisu (DOCX)', 'Count report (DOCX)')}</strong>
                                <p style={{ color: '#888', fontSize: '0.78rem', margin: '4px 0 8px' }}>
                                    {t('Generira se automatski kod predaje popisa, iz snimke rekonsilijacije. Ispisuje se, potpisuje i vraća skenirano niže.', 'Generated automatically at submission, from the reconciliation snapshot. Print it, sign it and upload the scan below.')}
                                </p>
                                <button type="button" disabled={detail.status === 'draft'} onClick={() => void getZapisnikDocx()} style={{ ...BTN, opacity: detail.status === 'draft' ? 0.5 : 1 }}>
                                    ⬇ {t('Preuzmi zapisnik', 'Download report')}
                                </button>
                            </div>

                            <div style={card}>
                                <strong style={{ color: '#2f4d2f' }}>{t('Potpisani zapisnik (PDF/slika)', 'Signed report (PDF/image)')}</strong>
                                {detail.zapisnik_pdf_path ? <div style={{ color: '#1f7a36', fontSize: '0.82rem', marginTop: 4 }}>{t('Već učitano.', 'Already uploaded.')}</div> : null}
                                <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setZapisnikFile(e.target.files?.[0] ?? null)} />
                                    <button type="button" disabled={!zapisnikFile || uploadingZapisnik} onClick={() => void uploadZapisnik()} style={{ ...BTN, opacity: zapisnikFile ? 1 : 0.5 }}>{uploadingZapisnik ? '…' : '⬆ ' + t('Učitaj', 'Upload')}</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
